/* IC Klaviyo Guard — defensive safety net for the app-injected Klaviyo popup.
 *
 * Confirmed bug (mobile + desktop): after the Klaviyo popup is dismissed, its
 * full-viewport container (div[role="dialog"].kl-private-reset-css-*, inline
 * position:fixed; z-index:90000) stays in the DOM at opacity:0 with
 * pointer-events:auto, and Klaviyo's reset CSS forces pointer-events:auto on
 * every descendant. Result: an invisible layer swallows every tap (Checkout,
 * Add to cart, everything) and body keeps .klaviyo-prevent-body-scrolling so
 * the page cannot scroll.
 *
 * This guard:
 *  - watches the DOM (MutationObserver + 400ms backup sweep),
 *  - marks any Klaviyo popup layer that is NOT visibly open with
 *    data-ic-kl-guard="blocked" (a stylesheet forces pointer-events:none on it
 *    AND all descendants, beating Klaviyo's reset CSS),
 *  - instantly unmarks a layer the moment it is genuinely open / fading in,
 *    so a real form stays fully interactive (fields, submit, close X),
 *  - releases Klaviyo's body scroll lock when no form is actually open.
 * It never removes Klaviyo nodes and never touches an open form.
 */
(function () {
  'use strict';

  if (window.__icKlaviyoGuardActive) return;
  window.__icKlaviyoGuardActive = true;

  var ATTR = 'data-ic-kl-guard';
  var STYLE_ID = 'ic-kl-guard-style';
  var SCROLL_LOCK_CLASS = 'klaviyo-prevent-body-scrolling';
  var KL_MARKER = 'kl-private-reset-css';

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      'html body [' + ATTR + '="blocked"],' +
      'html body [' + ATTR + '="blocked"] *{pointer-events:none !important;}';
    (document.head || document.documentElement).appendChild(st);
  }

  function className(el) {
    return typeof el.className === 'string' ? el.className : '';
  }

  function isKlaviyoDialog(el) {
    var cls = className(el);
    if (cls.indexOf(KL_MARKER) !== -1 || cls.indexOf('klaviyo') !== -1) return true;
    try {
      return !!el.querySelector('[class*="' + KL_MARKER + '"],[data-testid*="klaviyo"],.klaviyo-close-form');
    } catch (e) {
      return false;
    }
  }

  /* A layer counts as OPEN if it is displayed, has real size, and is either
   * actually visible (opacity > 0.02) or currently fading IN — so we never
   * block a form that is in the middle of opening. */
  function isVisiblyOpen(el) {
    if (!el.isConnected) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    var rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    var opacity = parseFloat(cs.opacity);
    if (isNaN(opacity) || opacity > 0.02) return true;
    return /fadein/i.test(cs.animationName || '') && cs.animationPlayState === 'running';
  }

  function collectTargets() {
    var targets = [];
    var i, el;

    /* 1) Klaviyo modal/teaser layers exposed as dialogs. */
    var dialogs = document.querySelectorAll('div[role="dialog"]');
    for (i = 0; i < dialogs.length; i++) {
      if (isKlaviyoDialog(dialogs[i])) targets.push(dialogs[i]);
    }

    /* 2) Generic fallback: any full-viewport fixed Klaviyo layer that is not
     *    inside one of the dialogs above (backdrops/overlays in other builds). */
    var overlays = document.querySelectorAll('div[class*="' + KL_MARKER + '"]');
    for (i = 0; i < overlays.length; i++) {
      el = overlays[i];
      if (el.closest('[role="dialog"]')) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed') continue;
      var rect = el.getBoundingClientRect();
      if (rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9) {
        targets.push(el);
      }
    }
    return targets;
  }

  function applyGuard() {
    try {
      ensureStyle();
      var anyOpen = false;
      var targets = collectTargets();
      for (var i = 0; i < targets.length; i++) {
        var el = targets[i];
        if (isVisiblyOpen(el)) {
          anyOpen = true;
          /* Open form: make sure it is fully interactive. */
          if (el.getAttribute(ATTR) === 'blocked') el.removeAttribute(ATTR);
        } else if (el.getAttribute(ATTR) !== 'blocked') {
          /* Dismissed/hidden layer: neutralise it so it can never eat a tap. */
          el.setAttribute(ATTR, 'blocked');
        }
      }
      /* Release Klaviyo's scroll lock if no form is actually open. */
      if (!anyOpen && document.body && document.body.classList.contains(SCROLL_LOCK_CLASS)) {
        document.body.classList.remove(SCROLL_LOCK_CLASS);
      }
    } catch (e) {
      /* Never let the guard break the storefront. */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      applyGuard();
    });
  }

  function start() {
    applyGuard();
    try {
      var mo = new MutationObserver(schedule);
      mo.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    } catch (e) {
      /* observer unavailable — interval below still covers us */
    }
    /* Low-frequency backup sweep. */
    window.setInterval(applyGuard, 400);
    /* Unblock instantly the moment Klaviyo starts fading a form in. */
    document.addEventListener('animationstart', function (e) {
      if (/fadein/i.test(e.animationName || '')) schedule();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
